import {
	ArrowPathIcon,
	ChevronLeftIcon,
	CircleStackIcon,
	EnvelopeIcon,
	PaperAirplaneIcon,
	ServerStackIcon
} from '@heroicons/react/24/outline'
import Client from 'clients/base/Client'
import { useCore } from 'contexts/core-context'
import { useEffect, useMemo, useState } from 'react'
import { showToast } from 'utilities/showToast'

import ManualEmailSendModal from './ManualEmailSendModal'
import ManualSgDataPullModal from './ManualSgDataPullModal'

const serviceIconMap = {
	sg_portal: CircleStackIcon,
	api_server: ServerStackIcon,
	daily_emails: EnvelopeIcon
}

const serviceToneMap = {
	ok: {
		icon: 'text-emerald-600',
		dot: 'bg-emerald-500',
		tooltip: 'bg-emerald-600'
	},
	scheduled: {
		icon: 'text-amber-600',
		dot: 'bg-amber-500',
		tooltip: 'bg-amber-600'
	},
	issue: {
		icon: 'text-rose-600',
		dot: 'bg-rose-500',
		tooltip: 'bg-rose-600'
	},
	loading: {
		icon: 'text-slate-500',
		dot: 'bg-slate-400',
		tooltip: 'bg-slate-700'
	}
}

const tooltipBody = service => {
	if (!service) return 'Checking current service status.'

	return [service.label, service.summary, service.detail].filter(Boolean).join(' | ')
}

const readableMessage = message => {
	if (!message) return ''

	try {
		const parsed = JSON.parse(message)
		return parsed?.message || message
	} catch {
		return message
	}
}

const defaultServices = [
	{ key: 'sg_portal', label: 'VGT Portal', status: 'loading', summary: 'Checking current status.' },
	{ key: 'api_server', label: 'API Server', status: 'loading', summary: 'Checking current status.' },
	{ key: 'daily_emails', label: 'GP Email Scheduler', status: 'loading', summary: 'Checking current status.' }
]

const OperationsStatusSidebar = () => {
	const { executeLogout, isLoggedIn } = useCore()
	const [collapsed, setCollapsed] = useState(false)
	const [canViewSidebar, setCanViewSidebar] = useState(false)
	const [hasResolvedPermissions, setHasResolvedPermissions] = useState(false)
	const [statusPayload, setStatusPayload] = useState(null)
	const [manualModalOpen, setManualModalOpen] = useState(false)
	const [manualActiveAction, setManualActiveAction] = useState(null)
	const [manualSgModalOpen, setManualSgModalOpen] = useState(false)
	const [manualSgActiveAction, setManualSgActiveAction] = useState(null)
	const [statusRefreshToken, setStatusRefreshToken] = useState(0)

	useEffect(() => {
		if (!isLoggedIn) {
			setCanViewSidebar(false)
			setHasResolvedPermissions(true)
			setStatusPayload(null)
			return
		}

		let isActive = true

		Client.OwnerAdminApi.permissions({
			status200: response => {
				if (!isActive) return

				const allowed = !!response?.canManageLocations
				setCanViewSidebar(allowed)
				setHasResolvedPermissions(true)

				if (!allowed) {
					setStatusPayload(null)
				}
			},
			else: statusCode => {
				if (!isActive) return
				if (statusCode === 401) {
					executeLogout()
					return
				}

				setCanViewSidebar(false)
				setHasResolvedPermissions(true)
				setStatusPayload(null)
			},
			error: error => {
				console.error(error)
				if (!isActive) return
				setCanViewSidebar(false)
				setHasResolvedPermissions(true)
				setStatusPayload(null)
			}
		})

		return () => {
			isActive = false
		}
	}, [executeLogout, isLoggedIn])

	useEffect(() => {
		if (!isLoggedIn || !canViewSidebar) return

		let isActive = true

		const loadStatus = () => {
			Client.OwnerApi.getOperationsStatus({
				status200: response => {
					if (!isActive) return
					setStatusPayload(response || null)
				},
				status401: () => {
					if (!isActive) return
					executeLogout()
				},
				else: statusCode => {
					if (!isActive) return
					if (statusCode === 403) {
						setCanViewSidebar(false)
						setStatusPayload(null)
					}
				},
				error: error => {
					console.error(error)
					if (!isActive) return
					setStatusPayload(null)
				}
			})
		}

		loadStatus()
		const intervalId = window.setInterval(loadStatus, 60000)

		return () => {
			isActive = false
			window.clearInterval(intervalId)
		}
	}, [canViewSidebar, executeLogout, isLoggedIn, statusRefreshToken])

	const sidebarServices = useMemo(() => {
		if (statusPayload?.services?.length > 0) return statusPayload.services

		return defaultServices
	}, [statusPayload])

	const manualSendDetails = statusPayload?.manualSend || null
	const manualSgPullDetails = statusPayload?.manualSgPull || null

	const refreshStatus = () => setStatusRefreshToken(current => current + 1)

	const handleManualSend = (action, notifyAdmins) => {
		setManualActiveAction(action)

		Client.OwnerApi.manualSendEmails(
			{ notifyAdmins },
			{
				status200: response => {
					const notificationFailed = response?.notification?.status === 'failed'
					const baseContent =
						response?.queuedCount > 0
							? `Queued ${response.queuedCount} report email(s) and dispatched ${response?.dispatch?.claimedCount || 0} for delivery.`
							: 'No new report emails were queued because the current report date is already covered.'

					if (notificationFailed) {
						showToast({
							intent: 'error',
							title: 'Manual Send Completed',
							content: `${baseContent} Admin notification failed: ${response?.notification?.error || 'Unknown error.'}`
						})
					} else {
						showToast({
							intent: 'success',
							title: notifyAdmins ? 'Emails Sent And Admin Notified' : 'Emails Sent Manually',
							content: baseContent
						})
					}

					setManualModalOpen(false)
					refreshStatus()
				},
				status401: () => {
					showToast({
						intent: 'error',
						title: 'Manual Email Send',
						content: 'Your session expired. Please sign in again.'
					})
					executeLogout()
				},
				status409: message => {
					showToast({
						intent: 'error',
						title: 'Manual Email Send',
						content: readableMessage(message) || 'A manual email send is already in progress.'
					})
				},
				else: () => {
					showToast({
						intent: 'error',
						title: 'Manual Email Send',
						content: 'Unable to start the manual email send.'
					})
				},
				error: error => {
					console.error(error)
					showToast({
						intent: 'error',
						title: 'Manual Email Send',
						content: 'Unable to start the manual email send.'
					})
				}
			},
			{
				onApiResponse: () => setManualActiveAction(null)
			}
		)
	}

	const handleManualSgPull = (action, notifyAdmins) => {
		setManualSgActiveAction(action)

		Client.OwnerApi.manualUpdatePortalData(
			{ notifyAdmins },
			{
				status200: response => {
					const notificationFailed = response?.notification?.status === 'failed'
					const baseContent =
						response?.finalStatus === 'promoted'
							? `Portal data updated successfully from batch #${response?.batchId}.`
							: `Manual portal update completed with batch #${response?.batchId} in ${response?.finalStatus || 'unknown'} status.`

					if (notificationFailed) {
						showToast({
							intent: 'error',
							title: 'Portal Data Updated',
							content: `${baseContent} Admin notification failed: ${response?.notification?.error || 'Unknown error.'}`
						})
					} else {
						showToast({
							intent: 'success',
							title: notifyAdmins ? 'Portal Data Updated And Admin Notified' : 'Portal Data Updated',
							content: baseContent
						})
					}

					setManualSgModalOpen(false)
					refreshStatus()
				},
				status401: () => {
					showToast({
						intent: 'error',
						title: 'Manual Portal Update',
						content: 'Your session expired. Please sign in again.'
					})
					executeLogout()
				},
				status409: message => {
					showToast({
						intent: 'error',
						title: 'Manual Portal Update',
						content: readableMessage(message) || 'A manual portal update is already in progress.'
					})
				},
				else: () => {
					showToast({
						intent: 'error',
						title: 'Manual Portal Update',
						content: 'Unable to start the manual portal data update.'
					})
				},
				error: error => {
					console.error(error)
					showToast({
						intent: 'error',
						title: 'Manual Portal Update',
						content: 'Unable to start the manual portal data update.'
					})
				}
			},
			{
				onApiResponse: () => setManualSgActiveAction(null)
			}
		)
	}

	if (!isLoggedIn || !hasResolvedPermissions || !canViewSidebar) return null

	return (
		<>
			<div
				className={`fixed right-0 top-1/2 z-40 hidden -translate-y-1/2 transition-transform duration-200 lg:block ${
					collapsed ? 'translate-x-[96px]' : 'translate-x-0'
				}`}
			>
				<button
					type="button"
					className={`absolute right-[95px] top-1/2 flex -translate-y-1/2 items-center justify-center rounded-l-xl border border-r-0 border-slate-200 px-3 py-6 shadow-sm transition ${
						collapsed ? 'bg-[#0065ee] text-white' : 'bg-slate-100 text-slate-700'
					}`}
					onClick={() => setCollapsed(current => !current)}
					aria-label={collapsed ? 'Show service sidebar' : 'Hide service sidebar'}
					title={collapsed ? 'Show' : 'Hide'}
				>
					<ChevronLeftIcon className={`h-5 w-5 transition-transform ${collapsed ? 'rotate-180' : 'rotate-0'}`} />
				</button>

				<div className="flex w-[96px] flex-col items-center justify-start overflow-visible rounded-l-xl border border-slate-200 bg-white shadow-xl">
					{sidebarServices.map(service => {
						const Icon = serviceIconMap[service.key] || ServerStackIcon
						const tone = serviceToneMap[service.status] || serviceToneMap.loading
						const tooltipText = tooltipBody(service)

						return (
							<div
								key={service.key}
								className="group relative flex w-full flex-col items-center border-b border-slate-200 px-2 py-3 last:border-b-0"
								title={tooltipText}
							>
								<div className="relative">
									<Icon className={`h-7 w-7 ${tone.icon}`} />
									<span className={`absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full ${tone.dot}`} />
								</div>
								<span className="mt-2 text-center text-[11px] font-medium leading-4 text-slate-700">
									{service.label}
								</span>

								<div
									className={`pointer-events-none absolute right-[calc(100%+10px)] top-1/2 z-50 hidden w-64 -translate-y-1/2 rounded-md px-3 py-2 text-xs leading-5 text-white shadow-lg group-hover:block ${tone.tooltip}`}
								>
									{tooltipText}
								</div>
							</div>
						)
					})}

					<div className="w-full border-t border-slate-200 bg-white p-2">
						<div className="space-y-2">
							<button
								type="button"
								className="flex w-full flex-col items-center justify-center rounded-lg bg-[#E95691] px-2 py-2 text-center text-[11px] font-semibold leading-4 text-white shadow-sm transition hover:bg-[#cc3d74] hover:text-white"
								onClick={() => setManualSgModalOpen(true)}
							>
								<ArrowPathIcon className="mb-1 h-5 w-5" />
								<span className="whitespace-nowrap">Update VGT</span>
								<span>Data</span>
							</button>
							<button
								type="button"
								className="flex w-full flex-col items-center justify-center rounded-lg bg-[#3E63F4] px-2 py-2 text-center text-[11px] font-semibold leading-4 text-white shadow-sm transition hover:bg-[#0b2986]"
								onClick={() => setManualModalOpen(true)}
							>
								<PaperAirplaneIcon className="mb-1 h-5 w-5" />
								<span>Email</span>
								<span>Manually</span>
							</button>
						</div>
					</div>
				</div>
			</div>

			<ManualSgDataPullModal
				visible={manualSgModalOpen}
				details={manualSgPullDetails}
				activeAction={manualSgActiveAction}
				onClose={() => {
					if (!manualSgActiveAction) {
						setManualSgModalOpen(false)
					}
				}}
				onUpdate={() => handleManualSgPull('update', false)}
				onUpdateAndNotify={() => handleManualSgPull('update_and_notify', true)}
			/>
			<ManualEmailSendModal
				visible={manualModalOpen}
				details={manualSendDetails}
				activeAction={manualActiveAction}
				onClose={() => {
					if (!manualActiveAction) {
						setManualModalOpen(false)
					}
				}}
				onSend={() => handleManualSend('send', false)}
				onSendAndNotify={() => handleManualSend('send_and_notify', true)}
			/>
		</>
	)
}

export default OperationsStatusSidebar
